/**
 * ============================================================================
 * FILTER OPTIONS — data-driven, CASCADING dropdown values (QuickSight parity).
 * ============================================================================
 *
 * The CTS QuickSight dashboard populates every dimension dropdown FROM THE
 * DATASET and CASCADES them (cascadingControlConfiguration): the options for
 * one control narrow by all OTHER selected filters — never by the control's
 * own selection (so multi-select within a control stays possible).
 *
 * Our equivalent, computed client-side from the cached getMetrics rowset:
 *   options(P) = distinct values of P across DCRs that pass
 *                (network scope) AND (all active tokens EXCEPT tokens on P)
 *
 * Static options exist only for the same controls QuickSight keeps static
 * (Report Period, Metric Group) — see filterConfig.ts.
 *
 * Source of truth: CollectionTrackerServiceCDK lib/quicksightStack.ts
 * (executive-summary-production sheet filterControls + buildCascadingSources).
 */

import { DCR_FIELD_ACCESSORS, buildDcrPredicate } from 'src/features/api/filterPredicate';
import { FilterQuery } from 'src/features/dashboard/dashboardSlice';
import { matchesDemandCategory } from 'src/config/demandCategories';
import { GetMetricsOutput, DcrRecord } from 'src/types/metricsApi.types';

export interface FilterOption {
  propertyKey: string;
  value: string;
}

export interface CascadeContext {
  /** Active filter tokens; a property's own tokens never narrow its options. */
  query?: FilterQuery;
  /** Demand-category entry id of the current page ('all'/undefined = no scope). */
  demandCategoryId?: string;
}

/**
 * Distinct values per filterable DCR field, first-seen order, cascaded by
 * the other active filters + network scope (QuickSight behavior).
 */
export function selectFilterOptions(
  responses: GetMetricsOutput[],
  cascade: CascadeContext = {},
): FilterOption[] {
  const allDcrs: DcrRecord[] = [];
  for (const response of responses) {
    for (const period of response.data ?? []) {
      allDcrs.push(...period.dcrData);
    }
  }
  const scoped =
    cascade.demandCategoryId && cascade.demandCategoryId !== 'all'
      ? allDcrs.filter((dcr) => matchesDemandCategory(cascade.demandCategoryId!, dcr.demandCategory))
      : allDcrs;

  const options: FilterOption[] = [];

  for (const propertyKey of Object.keys(DCR_FIELD_ACCESSORS)) {
    // Cascade rule: narrow by every OTHER property's tokens, never our own.
    const otherTokens = (cascade.query?.tokens ?? []).filter((t) => t.propertyKey !== propertyKey);
    const predicate = otherTokens.length
      ? buildDcrPredicate({ tokens: otherTokens, operation: cascade.query!.operation })
      : () => true;

    const seen = new Set<string>();
    for (const dcr of scoped) {
      if (!predicate(dcr)) continue;
      for (const raw of DCR_FIELD_ACCESSORS[propertyKey](dcr)) {
        const value = raw.trim();
        if (!value || seen.has(value)) continue;
        seen.add(value);
        options.push({ propertyKey, value });
      }
    }
  }

  return options;
}

// React hook wrapper (store access) lives at the component layer:
// src/components/filters/useFilterOptions.ts — this module stays pure.
